/**
 * Public Booking Service
 * API calls for the patient-facing online booking page.
 * These endpoints do not require authentication.
 */

export interface PracticeInfo {
  name: string;
  doctor_name: string;
  doctor_title: string;
  specialties: string | string[];
  logo_url: string | null;
  address: string;
  phone: string;
  email: string;
  city: string;
}

export interface BookingConfigInfo {
  slot_duration: number;
  max_advance_days: number;
  min_advance_hours: number;
  page_title: string;
  welcome_message: string;
  booking_instructions: string;
  primary_color: string;
  require_phone: boolean;
  require_email: boolean;
  confirmation_message: string;
}

export interface BookingTreatment {
  id: string;
  name: string;
  duration: number;
  cost: string;
  color: string;
}

export interface BookingInfoResponse {
  practice: PracticeInfo;
  config: BookingConfigInfo;
  treatments: BookingTreatment[];
}

export interface TimeSlot {
  start: string;
  end: string;
  available: boolean;
}

export interface CalendarDay {
  date: string;
  available: boolean;
  slot_count: number;
}

export interface BookingRequest {
  date: string;
  time: string;
  treatment_id?: string;
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  notes?: string;
}

export interface BookingResponse {
  success: boolean;
  appointment_id?: string;
  message?: string;
  error?: string;
  details?: {
    date: string;
    time: string;
    treatment: string;
    doctor: string;
    status: string;
  };
}

const BASE = '/api/appointments/booking/api';

async function apiFetch<T>(url: string): Promise<T> {
  const res = await fetch(url, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Request failed (${res.status})`);
  }
  return res.json();
}

export const bookingService = {
  async getInfo(): Promise<BookingInfoResponse> {
    return apiFetch<BookingInfoResponse>(`${BASE}/info/`);
  },

  async getSlots(date: string): Promise<{ date: string; slots: TimeSlot[] }> {
    return apiFetch(`${BASE}/slots/?date=${encodeURIComponent(date)}`);
  },

  async getCalendar(weeks = 4): Promise<{ dates: CalendarDay[] }> {
    return apiFetch(`${BASE}/calendar/?weeks=${weeks}`);
  },

  async createBooking(data: BookingRequest): Promise<BookingResponse> {
    const res = await fetch(`${BASE}/book/`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(data),
    });
    return res.json();
  },
};
